// src/pages/driverReports/CarWiseStatementUI.js
import React, { useState, useMemo, useEffect, useRef } from "react";
import CarPLSheet from "./statementComponents/CarPLSheet";
import CarSummary from "./statementComponents/CarSummary";
import useCarSheetsLoader from "./carSheets/useCarSheetsLoader";
import { usePermissions } from "react-admin";
import { 
  exportAllSheetsToExcel,
  exportBalancesByTab
} from "./carSheets/exportUtils";
import { createManualDriverSheet } from "./carSheets/buildSheets/manualDriverSheets";
import { useGetList } from "react-admin";
import { loadAssignmentsMap } from "../earningsImport/assignmentUtils";
import { finalizeSheetTotals } from "./carSheets/helpers";
import {
  applyDriverCostAutomationToSheets,
  loadInstallmentAdjustments,
} from "./carSheets/buildSheets/autoDriverCosts";



const ACTIVE_TAB_STORAGE_KEY = "carWiseStatementActiveTab";

function sortSheetsCarWise(list = []) {
  return [...list].sort((a, b) => {
    const aCar = String(a?.carNumber || "").trim();
    const bCar = String(b?.carNumber || "").trim();

    const carCompare = aCar.localeCompare(bCar, undefined, {
      numeric: true,
      sensitivity: "base",
    });

    if (carCompare !== 0) return carCompare;

    const aStart = a?.periodStart ? new Date(a.periodStart).getTime() : 0;
    const bStart = b?.periodStart ? new Date(b.periodStart).getTime() : 0;

    return aStart - bStart;
  });
}

function cloneSheetListForPreview(list = []) {
  try {
    return JSON.parse(JSON.stringify(list || []));
  } catch {
    return (list || []).map((x) => ({ ...x }));
  }
}

export default function CarWiseStatementUI() {
  // Get user permissions from react-admin
  const { permissions } = usePermissions();
  
  // Convert permissions to lowercase and check if user is admin
  const userRole = (permissions || "").toLowerCase();
  const isAdmin = ["admin", "super_admin"].includes(userRole);
 // <-- This will be true only for admin users
  
  
 useEffect(() => {
  console.log("Current user role:", userRole, "Is admin:", isAdmin);
}, [userRole, isAdmin]); // ✅ Added isAdmin
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [shouldBuild, setShouldBuild] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
const [refreshMode, setRefreshMode] = useState(false); // false = load, true = refresh
const [orphanedNotification, setOrphanedNotification] = useState(null);
const [asOfDate, setAsOfDate] = useState("");
const [asOfSheets, setAsOfSheets] = useState(null);
const [asOfLoading, setAsOfLoading] = useState(false);

  // ⭐ Load ALL sheets for this month (FS + Income Recalc)
  // ⭐ CHANGE TO THIS:
const { carSheets: sheets, loading, error, applyCarryForwardOnce } = useCarSheetsLoader(
  month, 
  shouldBuild,
  setShouldBuild,
  shouldLoad,
  setShouldLoad
);

const { data: drivers = [] } = useGetList("drivers", {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: "fullName", order: "ASC" },
  filter: { status: "active" },
});
const { data: clients = [] } = useGetList("clients", {
  pagination: { page: 1, perPage: 500 },
  sort: { field: "name", order: "ASC" },
});

  // Remember tab selection
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "combined";
    return localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) || "combined";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
    }
  }, [activeTab]);

  // Scroll top when right panel sheet changes
  const rightPanelRef = useRef(null);
  useEffect(() => {
    if (rightPanelRef.current) {
      rightPanelRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedId]);

  // Clear build trigger
  useEffect(() => {
    if (shouldBuild) setShouldBuild(false);
  }, [shouldBuild]);
  // ⭐ ADD THIS: Clear load trigger
useEffect(() => {
  if (shouldLoad) setShouldLoad(false);
}, [shouldLoad]);

// Add this new useEffect - anywhere with other useEffect hooks is fine
useEffect(() => {
  // Check for orphaned income data in localStorage
  const stored = localStorage.getItem(`orphaned_${month}`);
  if (stored) {
    try {
      const data = JSON.parse(stored);
      setOrphanedNotification(data);
    } catch (e) {
      console.error("Error loading orphaned data:", e);
    }
  } else {
    // Clear notification if no data for this month
    setOrphanedNotification(null);
  }
}, [month]); // Re-run when month changes

useEffect(() => {
  let cancelled = false;

  const buildAsOfPreview = async () => {
    if (!asOfDate) {
      setAsOfSheets(null);
      setAsOfLoading(false);
      return;
    }

    if (!sheets || sheets.length === 0) {
      setAsOfSheets([]);
      setAsOfLoading(false);
      return;
    }

    try {
      setAsOfLoading(true);
      const [costAssignmentsMap, installmentAdjustments] = await Promise.all([
        loadAssignmentsMap(),
        loadInstallmentAdjustments(month),
      ]);

      const preview = cloneSheetListForPreview(sheets);
      applyDriverCostAutomationToSheets({
        sheets: preview,
        month,
        assignmentsMap: costAssignmentsMap,
        adjustments: installmentAdjustments,
        asOfDate,
        includeWps: true,
        wpsFeePerDriver: 25,
      });

      preview.forEach((sheet) => {
        try {
          finalizeSheetTotals(sheet);
        } catch (e) {
          console.warn("Could not finalize as-of preview sheet:", sheet?.id, e);
        }
      });

      if (!cancelled) setAsOfSheets(preview);
    } catch (e) {
      console.error("Failed to build as-of preview:", e);
      if (!cancelled) setAsOfSheets(null);
    } finally {
      if (!cancelled) setAsOfLoading(false);
    }
  };

  buildAsOfPreview();

  return () => {
    cancelled = true;
  };
}, [asOfDate, month, sheets]);

const sheetsForView = asOfDate && asOfSheets ? asOfSheets : sheets;

const handleCreateManualSheet = async () => {
  try {
    if (!isAdmin) return; // safety

    const driverName = window.prompt("Car number / manual sheet name?");
    if (!driverName) return;

    const company = window.prompt("Company? (Comfort Line / Lolia etc)");
    if (!company) return;

    const planType = window.prompt("Plan Type? (private / rent / commission etc)");
    if (!planType) return;

    const created = await createManualDriverSheet({
      month,
      driverName,
      company,
      planType,
    });

    // reload sheets so manual sheet appears in list
    setShouldLoad(true);

    // auto-open if id is available
    if (created?.id) setSelectedId(created.id);
  } catch (e) {
    alert(e?.message || "Failed to create manual sheet");
    console.error(e);
  }
};

  /* ------------------------------------------------------------------
     FILTER SHEETS BY TAB  (RESTORED WORKING VERSION)
  ------------------------------------------------------------------ */
const tabFilteredSheets = useMemo(() => {
  if (!sheetsForView) return [];

  return sheetsForView.filter((s) => {
    const company = String(s.company || s.driverCompany || "").toUpperCase();
    const type = String(s.planType || "").toUpperCase().trim();

    const isLolia = company.includes("LOLIA");
    const isComfortLine = company.includes("COMFORT LINE");

    switch (activeTab) {
      case "lolia":
        return isLolia && (type === "FIXED" || type === "MIXED");

      case "cll":
        return isComfortLine && (type === "FIXED" || type === "MIXED");

      case "salary":
        return type === "SALARY" || type === "COMMISSION" || type === "MIXED";

      default:
        return true; // combined
    }
  });
}, [sheetsForView, activeTab]);

  /* -------------------------------------------------------------
      SUMMARY SHEET
     ------------------------------------------------------------- */
  const summarySheetObject = useMemo(() => {
    if (!tabFilteredSheets || tabFilteredSheets.length === 0) return null;

    return {
      id: "summary",
      carNumber: "📊 SUMMARY",
      carModel: "(All cars in this tab)",
      monthLabel: tabFilteredSheets[0].monthLabel || month.toUpperCase(),
      periodLabel: "SUMMARY",
      planType: "SUMMARY",
      company: "",
      drivers: [],
    };
  }, [tabFilteredSheets, month]);

  /* -------------------------------------------------------------
      SEARCH FILTER AFTER TAB FILTER
     ------------------------------------------------------------- */
  const filteredSheetsWithoutSummary = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tabFilteredSheets;

    return tabFilteredSheets.filter((s) =>
      `${s.carNumber} ${s.carModel} ${s.periodLabel} ${s.company || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [search, tabFilteredSheets]);

  /* -------------------------------------------------------------
      FINAL LIST (summary + filtered)
     ------------------------------------------------------------- */
  const finalSheets = useMemo(() => {
  let arr = [];

  if (summarySheetObject) arr.push(summarySheetObject);
  arr = [...arr, ...sortSheetsCarWise(filteredSheetsWithoutSummary)];

  return arr;
}, [summarySheetObject, filteredSheetsWithoutSummary]);

  const activeSheet =
    finalSheets.find((s) => s.id === selectedId) || finalSheets[0];

  /* =============================================================
      RENDER UI
     ============================================================= */

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 40,
        display: "flex",
        flexDirection: "column",
        background: "#fff",
      }}
    >
      {/* HEADER */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}
      >
        <div>
          <label
            style={{
              fontSize: 13,
              display: "block",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            Month
          </label>

          <input
            type="month"
            value={month}
            onChange={(e) => {
              setSelectedId("");
              setMonth(e.target.value);
            }}
            style={{
              padding: 6,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 13,
            }}
          />
        </div>

        <div>
          <label
            style={{
              fontSize: 13,
              display: "block",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            As of date preview
          </label>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 13,
              }}
            />
            {asOfDate && (
              <button
                type="button"
                onClick={() => setAsOfDate("")}
                style={{ padding: "6px 8px", fontSize: 12 }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
            Preview only: changes installment amount on displayed sheets, not saved data.
          </div>
        </div>

                {/* BUILD BUTTON - ONLY FOR ADMIN */}
        {isAdmin && (
          <button
            disabled={loading}
            onClick={() => {
              if (window.confirm("⚠️ This will DELETE ALL income and reload fresh data.\nExpenses will be kept.\nContinue?")) {
                setRefreshMode(true); // Set to REFRESH mode
                setShouldBuild(true); // Trigger refresh
                setShouldLoad(false); // Cancel any load
              }
            }}
            style={{
              padding: "6px 12px",
              background: loading ? "#ccc" : "#d32f2f", // Red = danger
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: 13,
            }}
          >
            {loading && refreshMode ? "Refreshing..." : "Refresh Income Data"}
          </button>
        )}

        {isAdmin && (
  <button
    disabled={loading}
    onClick={handleCreateManualSheet}
    style={{
      padding: "6px 12px",
      background: loading ? "#ccc" : "#1976d2",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      cursor: loading ? "not-allowed" : "pointer",
      fontSize: 13,
      marginLeft: 8,
    }}
  >
    + Manual Sheet
  </button>
)}

        {isAdmin && orphanedNotification && (
  <button
    onClick={() => {
      // Open orphaned income manager in new tab
      window.open(`/orphaned-income/${month}`, '_blank');
    }}
    style={{
      marginLeft: 8,
      padding: "6px 12px",
      background: "#ff9800",
      color: "white",
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 13
    }}
  >
    Manage Orphaned Income
    {orphanedNotification.weekly + orphanedNotification.manual > 0 && (
      <span style={{
        marginLeft: 6,
        background: '#f44336',
        color: 'white',
        borderRadius: '50%',
        padding: '1px 6px',
        fontSize: 11
      }}>
        {orphanedNotification.weekly + orphanedNotification.manual}
      </span>
    )}
  </button>
)}

        {/* LOAD SHEETS BUTTON - FOR ALL USERS */}
        <button
          disabled={loading}
          onClick={() => {
            setRefreshMode(false); // Set to LOAD mode
            setShouldLoad(true);   // Trigger load
            setShouldBuild(false); // Cancel any refresh
          }}
          style={{
            padding: "6px 12px",
            background: loading ? "#ccc" : "#4caf50", // Green = safe
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: 13,
            marginLeft: isAdmin ? 8 : 0, // Adjust margin based on admin button visibility
          }}
        >
          {loading ? "Loading..." : "Load Sheets"}
        </button>
{isAdmin && (
  <button
    disabled={loading}
    onClick={async () => {
      if (
        !window.confirm(
          "Copy last month Balance + fixed costs ONE TIME only?\nIt will not change again on rebuild.\nContinue?"
        )
      ) return;

      try {
        await applyCarryForwardOnce();
        alert("✅ One-time carry forward applied.");
      } catch (e) {
        console.error(e);
        alert("❌ Failed: " + (e?.message || "Unknown error"));
      }
    }}
    style={{
      padding: "6px 12px",
      background: loading ? "#ccc" : "#1976d2",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      cursor: loading ? "not-allowed" : "pointer",
      fontSize: 13,
      marginLeft: 8,
    }}
  >
    Fetch Last Month Balance (One Time)
  </button>
)}

        {loading && (
  <span style={{ fontSize: 12, color: "#555" }}>
    Loading car statements...
  </span>
)}

{asOfDate && (
  <span style={{ fontSize: 12, color: asOfLoading ? "#b45309" : "#0d47a1" }}>
    {asOfLoading ? "Calculating as-of preview..." : `Showing installment preview as of ${asOfDate}`}
  </span>
)}

{error && (
  <span style={{ fontSize: 12, color: "red" }}>Error: {error}</span>
)}

{/* ⭐⭐ ADD ORPHANED INCOME NOTIFICATION HERE ⭐⭐ */}
{orphanedNotification && (
  <div style={{
    padding: 8,
    marginLeft: 10,
    background: '#fff3cd',
    border: '1px solid #ffeaa7',
    borderRadius: 4,
    fontSize: 12
  }}>
    ⚠️ {orphanedNotification.total} AED orphaned income found
    <button
      onClick={() => {
        console.log("Orphaned details:", orphanedNotification);
        alert(`Orphaned Income:\n\nWeekly: ${orphanedNotification.weekly} items\nManual: ${orphanedNotification.manual} items\nTotal: ${orphanedNotification.total} AED`);
      }}
      style={{
        marginLeft: 8,
        padding: '2px 6px',
        fontSize: 11,
        background: '#ff9800',
        color: 'white',
        border: 'none',
        borderRadius: 3,
        cursor: 'pointer'
      }}
    >
      Details
    </button>
    <button
      onClick={() => {
        localStorage.removeItem(`orphaned_${month}`);
        setOrphanedNotification(null);
      }}
      style={{
        marginLeft: 4,
        padding: '2px 6px',
        fontSize: 11,
        background: '#ccc',
        color: '#333',
        border: 'none',
        borderRadius: 3,
        cursor: 'pointer'
      }}
    >
      Dismiss
    </button>
  </div>
)}
</div>  {/* This is the existing closing </div> */}

      {/* TABS */}
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <button
          disabled={loading}
          onClick={() => setActiveTab("lolia")}
          style={tabStyle(activeTab === "lolia", loading)}
        >
          Lolia Fixed Base
        </button>

        <button
          disabled={loading}
          onClick={() => setActiveTab("cll")}
          style={tabStyle(activeTab === "cll", loading)}
        >
          Comfort Line Fixed Base
        </button>

        <button
          disabled={loading}
          onClick={() => setActiveTab("salary")}
          style={tabStyle(activeTab === "salary", loading)}
        >
          Salary & Commission
        </button>

        <button
          disabled={loading}
          onClick={() => setActiveTab("combined")}
          style={tabStyle(activeTab === "combined", loading)}
        >
          Combined
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
  {/* Export Current Tab */}
  <button
    disabled={loading || tabFilteredSheets.length === 0}
    onClick={() => {
      const sheetsToExport = tabFilteredSheets;
      if (sheetsToExport.length > 0) {
        exportAllSheetsToExcel(sheetsToExport, month);
      }
    }}
    style={{
      padding: "6px 12px",
      background: loading || tabFilteredSheets.length === 0 ? "#ccc" : "#0d47a1",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      cursor: loading || tabFilteredSheets.length === 0 ? "not-allowed" : "pointer",
      fontSize: 13,
    }}
  >
    Export Current Tab
  </button>

  {/* Export Payable / Receivable */}
{!loading && tabFilteredSheets.length > 0 && (
  <button
    onClick={() => exportBalancesByTab(tabFilteredSheets, month, activeTab)}
    style={{
      padding: "6px 12px",
      background: "#6a1b9a",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      cursor: "pointer",
      fontSize: 13,
    }}
  >
    Export Payable / Receivable
  </button>
)}
</div>


      {/* MAIN */}
      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* LEFT PANEL */}
        <div
          style={{
            width: 260,
            borderRight: "1px solid #ddd",
            paddingRight: 8,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <h3 style={{ marginBottom: 8 }}>Car / Period List</h3>

          <input
            type="text"
            placeholder="Search by car / model / company"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: 6,
              fontSize: 13,
              marginBottom: 8,
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />

          <div style={{ overflowY: "auto", flex: 1 }}>
            {finalSheets.map((sheet, index) => (
              <button
                key={`${sheet.id}-${index}`}
                onClick={() => setSelectedId(sheet.id)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "6px 8px",
                  marginBottom: 4,
                  borderRadius: 6,
                  border:
                    activeSheet && activeSheet.id === sheet.id
                      ? "1px solid #0d47a1"
                      : "1px solid #ddd",
                  background:
                    activeSheet && activeSheet.id === sheet.id
                      ? "#e3f2fd"
                      : "#f9fafb",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <strong>{sheet.carNumber}</strong>

                <div style={{ fontSize: 11, color: "#555" }}>
                  {sheet.carModel}
                </div>

                {sheet.company && (
                  <div style={{ fontSize: 11, color: "#777" }}>
                    {sheet.company}
                  </div>
                )}

                {sheet.planType === "SUMMARY" && (
                  <div style={{ fontSize: 11, color: "#880e4f" }}>
                    SUMMARY (Tab)
                  </div>
                )}

                {sheet.planType && sheet.planType !== "SUMMARY" && (
                  <div style={{ fontSize: 11, color: "#1b5e20" }}>
                    Plan: {sheet.planType}
                  </div>
                )}

                {sheet.drivers?.length > 0 && (
                  <div style={{ fontSize: 11, color: "#444" }}>
                    Drivers: {sheet.drivers.filter(Boolean).join(", ")}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div
          ref={rightPanelRef}
          style={{
            flex: 1,
            paddingLeft: 8,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {activeSheet ? (
            activeSheet.id === "summary" ? (
              <CarSummary
  sheets={tabFilteredSheets}
  month={month}
  drivers={drivers}
  clients={clients}
/>


            ) : (
              <CarPLSheet
  key={`${activeSheet.id}-${asOfDate || "full"}`}
  sheet={activeSheet}
  month={month}
  asOfDatePreview={asOfDate}
  onForceRebuild={() => setShouldBuild(true)}
/>

            )
          ) : (
            <p style={{ fontSize: 13, color: "#555" }}>
              {loading
                ? "Loading car statements..."
                : "No data available for this filter."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------
   TAB STYLE HELPER
------------------------------ */
function tabStyle(active, loading) {
  return {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #aaa",
    background: loading
      ? "#e0e0e0"
      : active
      ? "#0d47a1"
      : "#f1f1f1",
    color: loading ? "#aaa" : active ? "#fff" : "#333",
    cursor: loading ? "not-allowed" : "pointer",
    fontSize: 13,
    opacity: loading ? 0.6 : 1,
  };
}
